import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Shield, Download, CheckCircle, Phone, Mail, Building, Calendar, DollarSign } from 'lucide-react';
import { Button } from '../ui/button';

export const CertificateOfInsurancePage = () => {
  const certificateUrl = "https://customer-assets.emergentagent.com/job_fc77820c-9f14-4da9-9ea9-4a1499931e30/artifacts/2ltuzu9m_Screenshot%202026-02-02%20at%209.41.56%E2%80%AFAM.png";

  const coverageDetails = [
    {
      type: "Commercial General Liability",
      policyNumber: "N9BP534490",
      effective: "01/29/2026",
      expiration: "01/29/2027",
      limits: [
        { name: "Each Occurrence", amount: "$1,000,000" },
        { name: "Damage to Rented Premises", amount: "$50,000" },
        { name: "Medical Expense (Any one person)", amount: "$5,000" },
        { name: "Personal & Adv Injury", amount: "Included" },
        { name: "General Aggregate", amount: "$2,000,000" },
        { name: "Products - Comp/Op Agg", amount: "$2,000,000" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <Shield className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Certificate of Insurance</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Swift Move and Clean LLC is fully licensed and insured to protect you and your belongings during every move and cleaning service.
          </p>
        </div>

        {/* Trust Badges */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="shadow-lg border-0 text-center">
            <CardContent className="p-6">
              <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900">Licensed & Insured</h3>
              <p className="text-sm text-gray-600 mt-2">Full coverage for your peace of mind</p>
            </CardContent>
          </Card>
          <Card className="shadow-lg border-0 text-center">
            <CardContent className="p-6">
              <Shield className="h-10 w-10 text-blue-500 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900">$2M Aggregate Coverage</h3>
              <p className="text-sm text-gray-600 mt-2">Comprehensive liability protection</p>
            </CardContent>
          </Card>
          <Card className="shadow-lg border-0 text-center">
            <CardContent className="p-6">
              <Building className="h-10 w-10 text-orange-500 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900">Berkshire Hathaway</h3>
              <p className="text-sm text-gray-600 mt-2">Insured by a trusted provider</p>
            </CardContent>
          </Card>
        </div>

        {/* Company Information */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Insured Company Information</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Company Name</h3>
                <p className="text-gray-700 mb-4">Swift Move and Clean LLC</p>
                
                <h3 className="font-semibold text-gray-900 mb-2">Address</h3>
                <p className="text-gray-700">
                  130 West Brunswick Drive<br />
                  Columbus, IN 47201
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Insurance Provider</h3>
                <p className="text-gray-700 mb-4">Berkshire Hathaway Direct Insurance Company</p>
                
                <h3 className="font-semibold text-gray-900 mb-2">Producer</h3>
                <p className="text-gray-700">
                  BIBERK<br />
                  P.O. Box 113247<br />
                  Stamford, CT 06911
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Coverage Details */}
        {coverageDetails.map((coverage, index) => (
          <Card key={index} className="mb-8 shadow-lg border-0">
            <CardContent className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{coverage.type}</h2>
                <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
                  Active
                </span>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Shield className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Policy Number</p>
                    <p className="font-semibold text-gray-900">{coverage.policyNumber}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <Calendar className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Effective Date</p>
                    <p className="font-semibold text-gray-900">{coverage.effective}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="bg-orange-100 p-2 rounded-lg">
                    <Calendar className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Expiration Date</p>
                    <p className="font-semibold text-gray-900">{coverage.expiration}</p>
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-4">Coverage Limits</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {coverage.limits.map((limit, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                    <span className="text-gray-700">{limit.name}</span>
                    <span className="font-semibold text-gray-900">{limit.amount}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Additional Coverage Info */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Additional Coverage</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                <span className="text-gray-700">Professional Liability (Errors & Omissions)</span>
                <span className="font-semibold text-gray-900">Claims-Made</span>
              </div>
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                <span className="text-gray-700">Workers Compensation</span>
                <span className="font-semibold text-gray-900">Per Statute</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* View Certificate */}
        <Card className="mb-8 shadow-lg border-0 bg-gradient-to-r from-blue-50 to-green-50">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">View Official Certificate</h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Download or view our official ACORD Certificate of Liability Insurance issued on February 2, 2026.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => window.open(certificateUrl, '_blank')}
              >
                <Download className="h-4 w-4 mr-2" />
                View Certificate
              </Button>
              <Button 
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = certificateUrl;
                  link.download = 'Swift_Move_and_Clean_Certificate_of_Insurance.png';
                  link.click();
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Download Certificate
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Certificate Preview */}
        <Card className="mb-8 shadow-lg border-0 overflow-hidden">
          <CardContent className="p-0">
            <div className="bg-gray-100 p-4">
              <h3 className="text-lg font-semibold text-gray-900 text-center">Certificate Preview</h3>
            </div>
            <div className="p-4 bg-white">
              <img 
                src={certificateUrl} 
                alt="Certificate of Liability Insurance" 
                className="w-full rounded-lg shadow-md"
              />
            </div>
          </CardContent>
        </Card>

        {/* Contact for Verification */}
        <Card className="shadow-lg border-0 bg-gradient-to-r from-orange-50 to-yellow-50">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Need Verification?</h2>
            <p className="text-gray-700 mb-6">
              If you need additional documentation or verification of our insurance coverage, please contact us or our insurance provider directly.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Swift Move and Clean</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-blue-600" />
                    <a href="tel:8126694165" className="text-blue-600 hover:underline">(812) 669-4165</a>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-blue-600" />
                    <a href="mailto:info@swiftmoveandclean.com" className="text-blue-600 hover:underline">info@swiftmoveandclean.com</a>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Insurance Provider (BIBERK)</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-blue-600" />
                    <span className="text-gray-700">844-472-0967</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-blue-600" />
                    <span className="text-gray-700">customerservice@biBERK.com</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CertificateOfInsurancePage;
